import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle, Loader2, Upload, FileCheck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MerchantIntakePublicForm = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedAppId, setSubmittedAppId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({});
  const [uploading, setUploading] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ voided_check_or_bank_letter: { uploaded: false, files: [] }, drivers_license_or_gov_id: { uploaded: false, files: [] } });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/mp-intake/${token}`)
      .then(res => { setInfo(res.data); setLoading(false); })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'This intake link is invalid or no longer active.');
        setLoading(false);
      });
  }, [token]);

  const set = (key, v) => setData(prev => ({ ...prev, [key]: v }));
  const setOwner = (n, key, v) => setData(prev => ({ ...prev, [`owner${n}`]: { ...(prev[`owner${n}`] || {}), [key]: v } }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/mp-intake/${token}`, { pre_app_data: data });
      setSubmittedAppId(res.data?.application_id || '');
      setSubmitted(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (docKey, file) => {
    if (!file || !submittedAppId) return;
    setUploading(docKey);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BACKEND_URL}/api/mp-intake/${token}/upload?application_id=${submittedAppId}&document_type=${docKey}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadStatus(prev => ({
        ...prev,
        [docKey]: { uploaded: true, files: [...(prev[docKey]?.files || []), { filename: file.name }] }
      }));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  if (error) return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4"><div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center"><p className="text-red-600 font-medium">{error}</p></div></div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#081120] rounded-t-2xl px-6 py-5">
            <h1 className="text-xl font-bold text-white">Quidity Direct</h1>
            <p className="text-sm text-slate-300 mt-1">Merchant Processing — Submitted</p>
          </div>
          <div className="bg-white rounded-b-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-slate-900 mb-1">Thank You!</h2>
              <p className="text-slate-700">Your application has been submitted{info?.company_name ? ` to ${info.company_name}` : ''}. Their team will be in touch shortly.</p>
            </div>

            <div className="space-y-3 mt-6 border-t pt-6">
              <h3 className="text-sm font-bold text-slate-900 uppercase">Optional — Attach Supporting Documents</h3>
              <p className="text-xs text-slate-700">Speed up onboarding by uploading these now.</p>
              {[
                { key: 'voided_check_or_bank_letter', label: 'Voided Check or Bank Letter' },
                { key: 'drivers_license_or_gov_id', label: "Driver's License or Government ID" },
              ].map(doc => {
                const status = uploadStatus[doc.key] || {};
                const isUploading = uploading === doc.key;
                const inputId = `upload-${doc.key}`;
                return (
                  <div key={doc.key} className="border border-slate-300 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {status.uploaded ? <FileCheck className="h-4 w-4 text-emerald-600" /> : <Upload className="h-4 w-4 text-slate-600" />}
                        <span className="text-sm font-semibold text-slate-900">{doc.label}</span>
                        {status.uploaded && <span className="text-[10px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full font-semibold">Uploaded</span>}
                      </div>
                      <input id={inputId} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.heic,.webp"
                        onChange={e => handleUpload(doc.key, e.target.files?.[0])}
                        data-testid={`intake-upload-${doc.key}-input`} />
                      <label htmlFor={inputId} className={`cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${isUploading ? 'bg-slate-200 text-slate-500' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                        data-testid={`intake-upload-${doc.key}-btn`}>
                        {isUploading ? <><Loader2 className="h-3 w-3 animate-spin" /> Uploading...</> : <><Upload className="h-3 w-3" /> {status.uploaded ? 'Upload Another' : 'Choose File'}</>}
                      </label>
                    </div>
                    {(status.files || []).length > 0 && (
                      <ul className="text-xs text-slate-700 ml-6 mt-1 list-disc space-y-0.5">
                        {status.files.map((f, i) => <li key={i}>{f.filename}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const o1 = data.owner1 || {};
  const o2 = data.owner2 || {};
  const greetingName = info?.company_name || info?.broker_name || 'your broker';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-[#081120] rounded-t-2xl px-6 py-5">
          <h1 className="text-xl font-bold text-white">Quidity Direct</h1>
          <p className="text-sm text-slate-300 mt-1">Merchant Processing — Pre-Application</p>
        </div>
        <div className="bg-white rounded-b-2xl shadow-lg p-6 space-y-6 [&_label]:text-slate-900 [&_label]:font-semibold [&_label]:text-sm [&_input]:text-slate-900 [&_input]:font-medium [&_input]:border-slate-400 [&_input::placeholder]:text-slate-500 [&_textarea]:text-slate-900 [&_textarea]:font-medium [&_textarea]:border-slate-400" data-testid="mp-intake-form">
          <p className="text-sm text-slate-700 font-medium">You're submitting to <strong className="text-slate-900">{greetingName}</strong>.</p>
          <p className="text-xs text-slate-700 italic">** This is not the final step in the process. Our team will take this info and send an application to you for review. Your processing account can be set up a few days after you return your signed official application.</p>

          {/* BUSINESS INFORMATION */}
          <section>
            <h2 className="text-base font-bold text-slate-900 uppercase tracking-wide mb-3 pb-1 border-b-2 border-slate-400">Business Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Legal Business Name</Label><Input value={data.legal_business_name || ''} onChange={e => set('legal_business_name', e.target.value)} /></div>
              <div><Label>Federal Tax ID</Label><Input value={data.federal_tax_id || ''} onChange={e => set('federal_tax_id', e.target.value)} /></div>
              <div><Label>DBA Name</Label><Input value={data.dba_name || ''} onChange={e => set('dba_name', e.target.value)} /></div>
              <div><Label>Business Phone Number</Label><Input value={data.business_phone_number || ''} onChange={e => set('business_phone_number', e.target.value)} /></div>
              <div><Label>Physical Street Address</Label><Input value={data.physical_street_address || ''} onChange={e => set('physical_street_address', e.target.value)} /></div>
              <div><Label>Mobile Number</Label><Input value={data.mobile_number || ''} onChange={e => set('mobile_number', e.target.value)} /></div>
              <div><Label>City</Label><Input value={data.city || ''} onChange={e => set('city', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>State</Label><Input value={data.state || ''} onChange={e => set('state', e.target.value)} /></div>
                <div><Label>Zip</Label><Input value={data.zip || ''} onChange={e => set('zip', e.target.value)} /></div>
              </div>
              <div className="md:col-span-2"><Label>E-Mail Address</Label><Input type="email" value={data.email_address || ''} onChange={e => set('email_address', e.target.value)} /></div>
            </div>
          </section>

          {/* MAIL TO */}
          <section>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-2">Mail To:</h3>
            <div className="flex flex-wrap gap-4 mb-3 text-slate-900 font-medium">
              {['Business Address', 'Home Address', 'Other'].map(opt => (
                <label key={opt} className="flex items-center gap-1.5 text-sm cursor-pointer text-slate-900">
                  <input type="radio" name="mail_to" checked={data.mail_to === opt} onChange={() => set('mail_to', opt)} />
                  {opt}
                </label>
              ))}
            </div>
            {data.mail_to === 'Other' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label>City</Label><Input value={data.mail_to_other_city || ''} onChange={e => set('mail_to_other_city', e.target.value)} /></div>
                <div><Label>State</Label><Input value={data.mail_to_other_state || ''} onChange={e => set('mail_to_other_state', e.target.value)} /></div>
                <div><Label>Zip</Label><Input value={data.mail_to_other_zip || ''} onChange={e => set('mail_to_other_zip', e.target.value)} /></div>
              </div>
            )}
          </section>

          {/* OWNERSHIP INFORMATION */}
          <section>
            <h2 className="text-base font-bold text-slate-900 uppercase tracking-wide mb-3 pb-1 border-b-2 border-slate-400">Ownership Information</h2>
            {[[1, o1], [2, o2]].map(([n, o]) => (
              <div key={n} className="mb-5">
                <h4 className="text-sm font-bold text-slate-900 uppercase mb-2">Owner {n}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Primary Owner Name</Label><Input value={o.primary_owner_name || ''} onChange={e => setOwner(n, 'primary_owner_name', e.target.value)} /></div>
                  <div><Label>Social Security Number</Label><Input value={o.social_security_number || ''} onChange={e => setOwner(n, 'social_security_number', e.target.value)} /></div>
                  <div><Label>DOB (MM/DD/YYYY)</Label><Input value={o.dob || ''} onChange={e => setOwner(n, 'dob', e.target.value)} placeholder="MM/DD/YYYY" /></div>
                  <div><Label>Home Address</Label><Input value={o.home_address || ''} onChange={e => setOwner(n, 'home_address', e.target.value)} /></div>
                  <div><Label>City</Label><Input value={o.city || ''} onChange={e => setOwner(n, 'city', e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>State</Label><Input value={o.state || ''} onChange={e => setOwner(n, 'state', e.target.value)} /></div>
                    <div><Label>Zip</Label><Input value={o.zip || ''} onChange={e => setOwner(n, 'zip', e.target.value)} /></div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* ADDITIONAL INFORMATION / COMMENTS */}
          <section>
            <h2 className="text-base font-bold text-slate-900 uppercase tracking-wide mb-3 pb-1 border-b-2 border-slate-400">Additional Information / Comments</h2>
            <textarea rows={4} value={data.additional_comments || ''} onChange={e => set('additional_comments', e.target.value)} className="w-full px-3 py-2 text-sm text-slate-900 font-medium border border-slate-400 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500" />
          </section>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="mp-intake-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MerchantIntakePublicForm;
